// Interactive date-based Gantt chart for project phases. Positions each phase
// bar along a real time axis using its planned_start / planned_end, with month
// gridlines and a "today" marker. When `editable`, bars can be dragged to shift
// a phase (preserving its duration) and the left/right edges dragged to change
// the start/end date. Phases whose planned end has passed without being Complete
// are flagged in red ("late"). Pure CSS + pointer events, no extra deps.
import { useEffect, useRef, useState } from "react";
import { CalendarRange, AlertTriangle } from "lucide-react";

const STATUS_COLOR = {
    Complete: "#2E7D4F",
    Blocked: "#B33A2E",
    "In progress": "#2A5DB0",
    "Not started": "#9A8F7E",
};
const LATE_COLOR = "#D14328";

const DAY = 86400000;
const parse = (d) => (d ? new Date(`${d.slice(0, 10)}T00:00:00`) : null);
const fmt = (dt) => dt.toLocaleDateString("en-GB", { day: "2-digit", month: "short" });
const toISO = (dt) => {
    const y = dt.getFullYear();
    const m = String(dt.getMonth() + 1).padStart(2, "0");
    const d = String(dt.getDate()).padStart(2, "0");
    return `${y}-${m}-${d}`;
};
const startOfToday = () => { const t = new Date(); t.setHours(0, 0, 0, 0); return t; };

function monthTicks(start, end) {
    const ticks = [];
    const d = new Date(start.getFullYear(), start.getMonth(), 1);
    while (d <= end) {
        ticks.push(new Date(d));
        d.setMonth(d.getMonth() + 1);
    }
    return ticks;
}

export default function GanttTimeline({ stages = [], phaseProgress = {}, editable = false, onPhaseChange }) {
    // drag = { key, mode: 'move'|'start'|'end', startX, origS, origE, total, width, previewS, previewE }
    const [drag, setDrag] = useState(null);
    const dragRef = useRef(null);
    dragRef.current = drag;

    const dated = stages
        .map((s) => ({ ...s, _s: parse(s.planned_start), _e: parse(s.planned_end) }))
        .filter((s) => s._s && s._e);

    useEffect(() => {
        if (!drag) return;
        const onMove = (e) => {
            const d = dragRef.current;
            if (!d) return;
            const deltaDays = Math.round(((e.clientX - d.startX) * (d.total / d.width)) / DAY);
            let ns = new Date(d.origS), ne = new Date(d.origE);
            if (d.mode === "move") { ns = new Date(d.origS + deltaDays * DAY); ne = new Date(d.origE + deltaDays * DAY); }
            else if (d.mode === "start") { ns = new Date(Math.min(d.origS + deltaDays * DAY, d.origE)); }
            else if (d.mode === "end") { ne = new Date(Math.max(d.origE + deltaDays * DAY, d.origS)); }
            setDrag((cur) => (cur ? { ...cur, previewS: ns, previewE: ne } : cur));
        };
        const onUp = () => {
            const d = dragRef.current;
            setDrag(null);
            if (d && d.previewS && d.previewE) {
                const ns = toISO(d.previewS), ne = toISO(d.previewE);
                if ((ns !== toISO(new Date(d.origS)) || ne !== toISO(new Date(d.origE))) && onPhaseChange) {
                    onPhaseChange(d.key, { planned_start: ns, planned_end: ne });
                }
            }
        };
        window.addEventListener("pointermove", onMove);
        window.addEventListener("pointerup", onUp);
        return () => { window.removeEventListener("pointermove", onMove); window.removeEventListener("pointerup", onUp); };
    }, [drag, onPhaseChange]);

    if (dated.length === 0) {
        return (
            <div data-testid="gantt-empty" className="flex items-center gap-2 text-[11.5px] italic px-3 py-3 rounded-[9px]"
                style={{ background: "#FCFBF9", border: "1px dashed #E5E0DA", color: "#9A968F" }}>
                <CalendarRange className="w-3.5 h-3.5" />
                Add planned start &amp; end dates to phases to build the schedule timeline.
            </div>
        );
    }

    // domain includes any live drag preview so the bar stays visible while dragging
    let min = dated[0]._s, max = dated[0]._e;
    dated.forEach((s) => { if (s._s < min) min = s._s; if (s._e > max) max = s._e; });
    if (drag?.previewS && drag.previewS < min) min = drag.previewS;
    if (drag?.previewE && drag.previewE > max) max = drag.previewE;
    const span = Math.max(max - min, DAY);
    const domainStart = new Date(min - span * 0.04);
    const domainEnd = new Date(max.getTime() + span * 0.04);
    const total = domainEnd - domainStart || DAY;
    const pos = (dt) => ((dt - domainStart) / total) * 100;

    const ticks = monthTicks(domainStart, domainEnd);
    const today = new Date();
    const todayPct = today >= domainStart && today <= domainEnd ? pos(today) : null;
    const today0 = startOfToday();

    const beginDrag = (e, s, mode) => {
        if (!editable) return;
        e.preventDefault();
        e.stopPropagation();
        const track = e.currentTarget.closest("[data-gantt-track]");
        if (!track) return;
        setDrag({
            key: s.key, mode, startX: e.clientX,
            origS: s._s.getTime(), origE: s._e.getTime(),
            total, width: track.clientWidth || 1,
            previewS: new Date(s._s), previewE: new Date(s._e),
        });
    };

    return (
        <div data-testid="gantt-timeline" className="select-none">
            {/* month axis */}
            <div className="relative h-5 ml-[148px] mb-1" style={{ borderBottom: "1px solid #EFEAE3" }}>
                {ticks.map((t, i) => (
                    <div key={i} className="absolute top-0 text-[9px] font-semibold uppercase tracking-wide"
                        style={{ left: `${pos(t)}%`, color: "#B8B2A8" }}>
                        <span className="absolute top-0 -translate-x-px h-5 border-l" style={{ borderColor: "#F0EDE8" }} />
                        <span className="pl-1 whitespace-nowrap">{t.toLocaleDateString("en-GB", { month: "short", year: "2-digit" })}</span>
                    </div>
                ))}
            </div>

            <div className="relative space-y-1.5">
                {/* today marker spanning all rows */}
                {todayPct != null && (
                    <div data-testid="gantt-today" className="absolute top-0 bottom-0 z-10 pointer-events-none"
                        style={{ left: `calc(148px + ${todayPct}% * (100% - 148px) / 100)` }}>
                        <div className="w-px h-full" style={{ background: "#C75A1B", opacity: 0.55 }} />
                    </div>
                )}
                {dated.map((s) => {
                    const dragging = drag?.key === s.key;
                    const bs = dragging ? drag.previewS : s._s;
                    const be = dragging ? drag.previewE : s._e;
                    const left = pos(bs);
                    const width = Math.max(pos(be) - left, 1.5);
                    const pct = phaseProgress[s.key] ?? 0;
                    const late = be < today0 && s.status !== "Complete";
                    const color = late ? LATE_COLOR : (STATUS_COLOR[s.status] || "#9A8F7E");
                    const days = Math.max(Math.round((be - bs) / DAY) + 1, 1);
                    return (
                        <div key={s.key} data-testid={`gantt-row-${s.key}`} className="flex items-center gap-2 h-7">
                            <div className="w-[140px] shrink-0 text-[11px] font-semibold truncate flex items-center gap-1"
                                style={{ color: late ? LATE_COLOR : "#2D2D2D" }} title={s.label}>
                                {late && <AlertTriangle data-testid={`gantt-late-${s.key}`} className="w-3 h-3 shrink-0" />}
                                <span className="truncate">{s.label}</span>
                            </div>
                            <div data-gantt-track className="relative flex-1 h-5">
                                <div className="absolute inset-0 rounded" style={{ background: "#FAF8F5" }} />
                                <div data-testid={`gantt-bar-${s.key}`}
                                    onPointerDown={(e) => beginDrag(e, s, "move")}
                                    className={`absolute top-0 h-5 rounded-md overflow-hidden shadow-sm group ${editable ? (dragging ? "cursor-grabbing ring-2 ring-[#C75A1B]/60" : "cursor-grab") : ""}`}
                                    style={{ left: `${left}%`, width: `${width}%`, background: late ? "#F6D9D0" : "#EFEAE3" }}
                                    title={`${s.label}: ${fmt(bs)} → ${fmt(be)} (${days}d) · ${pct}%${late ? " · LATE" : ""}${editable ? " · drag to reschedule" : ""}`}>
                                    <div className="h-full pointer-events-none" style={{ width: `${pct}%`, background: color }} />
                                    <span className="absolute inset-0 flex items-center px-1.5 text-[9.5px] font-bold text-white mix-blend-luminosity pointer-events-none"
                                        style={{ textShadow: "0 1px 1px rgba(0,0,0,.25)" }}>{pct}%</span>
                                    {editable && (
                                        <>
                                            <span data-testid={`gantt-handle-start-${s.key}`}
                                                onPointerDown={(e) => beginDrag(e, s, "start")}
                                                className="absolute left-0 top-0 h-full w-1.5 cursor-ew-resize opacity-0 group-hover:opacity-100"
                                                style={{ background: "rgba(0,0,0,.28)" }} />
                                            <span data-testid={`gantt-handle-end-${s.key}`}
                                                onPointerDown={(e) => beginDrag(e, s, "end")}
                                                className="absolute right-0 top-0 h-full w-1.5 cursor-ew-resize opacity-0 group-hover:opacity-100"
                                                style={{ background: "rgba(0,0,0,.28)" }} />
                                        </>
                                    )}
                                </div>
                            </div>
                        </div>
                    );
                })}
            </div>

            <div className="flex items-center gap-4 mt-3 ml-[148px] flex-wrap text-[10px]" style={{ color: "#9A968F" }}>
                {Object.entries(STATUS_COLOR).map(([k, c]) => (
                    <span key={k} className="inline-flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-sm" style={{ background: c }} />{k}</span>
                ))}
                <span className="inline-flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-sm" style={{ background: LATE_COLOR }} />Late</span>
                {todayPct != null && <span className="inline-flex items-center gap-1"><span className="w-px h-3" style={{ background: "#C75A1B" }} /> Today</span>}
                {editable && <span className="italic">Drag a bar to reschedule · drag its edges to resize</span>}
            </div>
        </div>
    );
}
